import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'theme';

// localStorage はプライベートモード等で例外を投げることがあるので、失敗しても
// テーマ切り替え自体は動き続けるようにする（保存されないだけ）。
const readStoredTheme = () => {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
        return null;
    }
};

const storeTheme = (theme) => {
    try {
        localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
        /* 保存できなくても続行 */
    }
};

// public/index.html のインラインスクリプトが、描画前に <html> へ dark クラスを
// 付け終えている。判定をここで再実行すると二重管理になるので、その結果を読むだけにする。
const getInitialTheme = () =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light';

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(getInitialTheme);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark');
    }, [theme]);

    // 明示的に選択されていない間は OS の設定に追従する。トグルを押した時点で
    // localStorage に保存され、以降はユーザーの選択が優先される。
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event) => {
            if (readStoredTheme()) return;
            setTheme(event.matches ? 'dark' : 'light');
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme((prev) => {
            const next = prev === 'light' ? 'dark' : 'light';
            storeTheme(next);
            return next;
        });
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme は ThemeProvider の内側で使ってください。');
    }
    return context;
};
